import api from '../service/api';

const CHAVE_VERSAO = '@Aplication:logoVersion';
const EVENTO_ATUALIZADA = 'app:logo-empresa-atualizada';

export function lerVersaoLogo(): string {
  return localStorage.getItem(CHAVE_VERSAO) || '';
}

/**
 * Baixa a logomarca da empresa (a mesma que é enviada em "Cadastro Empresas")
 * e devolve um object URL (`blob:`) pronto para um <img>.
 *
 * Por que não devolver mais a URL da API direto, como era antes: quem busca a
 * imagem de um `<img src>` é o NAVEGADOR, e essa requisição não passa pelo
 * axios — ou seja, sai sem o header Authorization. Enquanto
 * `GET /empresa/:id/logo` era aberta isso funcionava; desde os commits
 * `Feat: isAuthenticated` da ESF-API ela exige token e responde 401, e a logo
 * simplesmente sumiu do Header. Baixando pelo axios a chamada leva o Bearer
 * (e ganha de brinde o retry com refresh do AuthContext).
 *
 * Devolve '' quando não há empresa, quando a empresa não tem logo cadastrada
 * (404) ou quando o download falha — o chamador trata como "sem logo".
 *
 * O `?v=` continua sendo cache-buster: a API responde com
 * `Cache-Control: private, max-age=3600`, então sem ele o navegador serviria a
 * logo antiga por até 1h depois de trocarem o arquivo.
 *
 * IMPORTANTE: quem chama vira dono do object URL devolvido e precisa liberá-lo
 * com `liberarLogo()` no cleanup, senão cada troca de logo deixa um blob preso
 * na memória.
 */
export async function baixarLogoEmpresa(
  empresaId?: string,
  versao = ''
): Promise<string> {
  // baseURL pode vir de variável de ambiente (REACT_APP_BASE_URL) e não estar
  // definida; sem essa guarda a URL sairia como "undefined/empresa/...".
  if (!empresaId || !api.defaults.baseURL) return '';

  const caminho = `/empresa/${empresaId}/logo`;

  try {
    const { data } = await api.get<Blob>(caminho, {
      responseType: 'blob',
      params: versao ? { v: versao } : undefined,
    });
    if (!data || !data.size) return '';
    return URL.createObjectURL(data);
  } catch {
    // Empresa sem logo cadastrada (404) ou falha de rede.
    return '';
  }
}

/** Libera um object URL devolvido por `baixarLogoEmpresa`. */
export function liberarLogo(url: string): void {
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
}

/**
 * Chamar depois que a API confirmar o upload da logo: grava uma versão nova e
 * avisa quem estiver exibindo a imagem (hoje o Header) para trocar na hora,
 * sem precisar de F5 nem de novo login. Devolve a versão gerada.
 */
export function notificarLogoAtualizada(): number {
  const versao = Date.now();
  localStorage.setItem(CHAVE_VERSAO, String(versao));
  window.dispatchEvent(new Event(EVENTO_ATUALIZADA));
  return versao;
}

/** Assina as trocas de logo (mesma aba via evento próprio, outras abas via
 * `storage`). Devolve a função de cancelamento para usar no cleanup do effect. */
export function ouvirLogoAtualizada(handler: () => void): () => void {
  const handlerStorage = (e: StorageEvent) => {
    if (e.key === CHAVE_VERSAO) handler();
  };

  window.addEventListener(EVENTO_ATUALIZADA, handler);
  window.addEventListener('storage', handlerStorage);

  return () => {
    window.removeEventListener(EVENTO_ATUALIZADA, handler);
    window.removeEventListener('storage', handlerStorage);
  };
}

/**
 * Devolve a imagem sem a moldura transparente em volta. A logo da Valeza, por
 * exemplo, é um PNG quadrado com ~28% de vazio embaixo: com ele o `objectFit`
 * encolhe a marca e joga ela pra cima, desalinhada do logo da essencial ao
 * lado. Recortar no canvas alinha altura e linha para qualquer arquivo que
 * venha de "Cadastro Empresas", sem número mágico no CSS. Qualquer falha
 * devolve a URL recebida e a logo continua aparecendo.
 *
 * Recebe o object URL de `baixarLogoEmpresa`, não mais o endereço da API. Duas
 * consequências: o `crossOrigin = 'anonymous'` saiu (um `blob:` é same-origin,
 * o canvas não fica "tainted") e não há mais cache por URL — cada download
 * gera um object URL novo, então o mapa só cresceria sem nunca dar acerto.
 */
export function recortarTransparencia(url: string): Promise<string> {
  return new Promise((resolve) => {
    const concluir = (src: string) => {
      resolve(src);
    };

    const img = new window.Image();

    img.onload = () => {
      try {
        const { naturalWidth: largura, naturalHeight: altura } = img;
        const canvas = document.createElement('canvas');
        canvas.width = largura;
        canvas.height = altura;

        const ctx = canvas.getContext('2d');
        if (!ctx) return concluir(url);

        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, largura, altura);

        let topo = altura;
        let base = -1;
        let esquerda = largura;
        let direita = -1;

        for (let y = 0; y < altura; y += 1) {
          for (let x = 0; x < largura; x += 1) {
            if (data[(y * largura + x) * 4 + 3] > 10) {
              if (y < topo) topo = y;
              if (y > base) base = y;
              if (x < esquerda) esquerda = x;
              if (x > direita) direita = x;
            }
          }
        }

        const larguraCorte = direita - esquerda + 1;
        const alturaCorte = base - topo + 1;

        // Sem transparência em volta (JPEG, PNG já aparado) não há o que fazer.
        if (
          larguraCorte <= 0 ||
          alturaCorte <= 0 ||
          (larguraCorte === largura && alturaCorte === altura)
        ) {
          return concluir(url);
        }

        const recorte = document.createElement('canvas');
        recorte.width = larguraCorte;
        recorte.height = alturaCorte;
        recorte
          .getContext('2d')
          ?.drawImage(
            canvas,
            esquerda,
            topo,
            larguraCorte,
            alturaCorte,
            0,
            0,
            larguraCorte,
            alturaCorte
          );

        concluir(recorte.toDataURL('image/png'));
      } catch {
        concluir(url);
      }
    };

    img.onerror = () => resolve(url);
    img.src = url;
  });
}
